import type { SessionUser } from "../../types/auth";

const SESSION_ENDPOINT = "/auth/session";

export interface SessionResponse {
  user: SessionUser | null;
  /** Server-computed from the admin email allowlist; never derive this client-side. */
  hasBackofficeAccess: boolean;
}

/**
 * One session request per page load, shared by every caller. Identity only changes through
 * sign-in/sign-out, and both are full page loads (see AuthContext.signIn/signOut), so nothing in a
 * single page load needs a second answer. Without this, AuthContext (mounted in a different
 * LayoutDefault branch per page type, so it remounts on SRP <-> job page navigations) and
 * useUserQuery each refetched /auth/session on every soft navigation.
 */
let sessionPromise: Promise<SessionResponse> | null = null;

async function requestSession(): Promise<SessionResponse> {
  const res = await fetch(SESSION_ENDPOINT, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    return { user: null, hasBackofficeAccess: false };
  }

  const data = (await res.json()) as { user: SessionUser | null; hasBackofficeAccess?: boolean };
  return { user: data?.user ?? null, hasBackofficeAccess: data?.hasBackofficeAccess ?? false };
}

/**
 * Fetches the same `/auth/session` endpoint `contexts/AuthContext.tsx` already uses, with the
 * same "never throw, fall back to signed-out" behavior for a non-ok response: a failed session
 * check should read as "not signed in", not surface as a query error the UI has to render an
 * error state for. A network failure still rejects (callers already handle it) and is not
 * cached, so the next caller retries.
 */
export function fetchSession(): Promise<SessionResponse> {
  if (typeof window === "undefined") return requestSession();
  if (!sessionPromise) {
    sessionPromise = requestSession();
    sessionPromise.catch(() => {
      sessionPromise = null;
    });
  }
  return sessionPromise;
}

/** Test-only: forget the cached session request. */
export function __resetSessionCacheForTests(): void {
  sessionPromise = null;
}
